import React, { useState, useEffect, useRef } from "react"
import {
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap"
import { useSelector } from "react-redux"
import Spinners from "components/Common/Spinner"
import { color } from "echarts"

const PostList = ({
  reviews = [],
  setPostClicked,
  setSelectedPost,
  loadMorePosts, // optional for infinite scroll
  loading, // optional loading spinner
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(5)
  const mode = useSelector(state => state.Layout.layoutMode)
  const containerRef = useRef(null)

  // Pagination logic
  const totalPages = Math.ceil(reviews.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentReviews = reviews.slice(startIndex, endIndex)

  const paginate = pageNumber => setCurrentPage(pageNumber)

  // Infinite Scroll (optional)
  useEffect(() => {
    if (!loadMorePosts) return

    const handleScroll = () => {
      if (
        containerRef.current &&
        containerRef.current.scrollTop + containerRef.current.clientHeight >=
          containerRef.current.scrollHeight - 10
      ) {
        loadMorePosts()
      }
    }

    const container = containerRef.current
    if (container) container.addEventListener("scroll", handleScroll)
    return () => {
      if (container) container.removeEventListener("scroll", handleScroll)
    }
  }, [loadMorePosts])

  const formatDate = dateString => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const truncateText = (text, maxLength) => {
    return text?.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text
  }

  return (
    <div
      style={{
        padding: "0px 20px",
        borderRadius: "20px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: mode === "dark" ? "#252f40" : "#fff",
        marginBottom: "20px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 20px 0 20px",
        }}
      >
        <h4
          style={{
            fontWeight: "600",
            fontSize: "16px",
            color: mode === "dark" ? "#9ca3af" : "#374151",
            margin: 0,
          }}
        >
          Posts
        </h4>
      </div>

      {/* Table */}
      <Row>
        <Col xl={12}>
          <div
            ref={containerRef}
            style={{
              backgroundColor: mode === "dark" ? "#252f40" : "#fff",
              overflowY: "auto",
              maxHeight: "400px",
              padding: "20px",
            }}
          >
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "120px 150px 1fr 150px 120px 120px",
                gap: "16px",
                padding: "16px 20px",
                backgroundColor: mode === "dark" ? "#1a2332" : "#f9fafb",
                borderBottom: `1px solid ${
                  mode === "dark" ? "#3a4556" : "#e5e7eb"
                }`,
                fontWeight: "600",
                fontSize: "14px",
                color: mode === "dark" ? "#9ca3af" : "#374151",
              }}
            >
              <div>Post ID</div>
              <div>Posted On</div>
              <div>Caption</div>
              <div>Media Attachments</div>
              <div>Reactions</div>
              <div>Comments</div>
            </div>

            {/* Table Body */}
            {loading ? (
              <div>
                <Spinners
                  style={{
                    color: mode === "dark" ? "#fff" : "#2990DF",
                  }}
                />
              </div>
            ) : currentReviews.length > 0 ? (
              currentReviews.map((review, index) => (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 150px 1fr 150px 120px 120px",
                    gap: "16px",
                    padding: "16px 20px",
                    borderBottom:
                      index < currentReviews.length - 1
                        ? `1px solid ${mode === "dark" ? "#3a4556" : "#e5e7eb"}`
                        : "none",
                    backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                    color: mode === "dark" ? "#d1d5db" : "#374151",
                    fontSize: "14px",
                    transition: "background-color 0.2s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      mode === "dark" ? "#2d3748" : "#f9fafb"
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor =
                      mode === "dark" ? "#252f40" : "#fff"
                  }}
                  onClick={() => {
                    if (setPostClicked && setSelectedPost) {
                      setPostClicked(true)
                      setSelectedPost(review.id)
                    }
                  }}
                >
                  {/* Post ID */}
                  <div>{truncateText(review.id || "-", 8)}</div>

                  {/* Posted On */}
                  <div>{formatDate(review.created_time)}</div>

                  {/* Caption */}
                  <div style={{ lineHeight: "1.5" }}>
                    {truncateText(review.message || "-", 70)}
                  </div>

                  {/* Media Attachments */}
                  <div style={{ textAlign: "center" }}>
                    {review.attachments?.data?.length || 0}
                  </div>

                  {/* Reactions */}
                  <div style={{ textAlign: "center" }}>
                    {review.reactions?.data?.length || 0}
                  </div>

                  {/* Comments */}
                  <div style={{ textAlign: "center" }}>
                    {review.comments?.data?.length || 0}
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: mode === "dark" ? "#9ca3af" : "#6b7280",
                }}
              >
                No data available
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "left",
          padding: "16px",
        }}
      >
        <Pagination className="mb-0 justify-content-center mt-auto">
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink
              previous
              onClick={() => paginate(currentPage - 1)}
              style={{
                color: mode === "dark" ? "#fff" : "#000",
                backgroundColor: "transparent",
                borderColor: mode === "dark" ? "#444" : "#dee2e6",
              }}
            />
          </PaginationItem>

          {Array.from({ length: totalPages }).map((_, i) => (
            <PaginationItem active={i + 1 === currentPage} key={i}>
              <PaginationLink
                onClick={() => paginate(i + 1)}
                style={{
                  borderColor:
                    i + 1 === currentPage
                      ? "#2990DF"
                      : mode === "dark"
                        ? "#444"
                        : "#dee2e6",
                  backgroundColor:
                    i + 1 === currentPage ? "#2990DF" : "transparent",
                  color:
                    i + 1 === currentPage
                      ? "#fff"
                      : mode === "dark"
                        ? "#fff"
                        : "#000",
                  boxShadow: "none",
                }}
              >
                {i + 1}
              </PaginationLink>
            </PaginationItem>
          ))}

          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink
              next
              onClick={() => paginate(currentPage + 1)}
              style={{
                color: mode === "dark" ? "#fff" : "#000",
                backgroundColor: "transparent",
                borderColor: mode === "dark" ? "#444" : "#dee2e6",
              }}
            />
          </PaginationItem>
        </Pagination>
      </div>
    </div>
  )
}

export default PostList
